import { css } from '@emotion/react';
import Divider from '@polestar/component-warehouse-react/components/divider/Divider';
import Grid from '@polestar/component-warehouse-react/components/grid/Grid';
import GridColumn from '@polestar/component-warehouse-react/components/grid/GridColumn';
import Link, { LinkVariants, } from '@polestar/component-warehouse-react/components/link/Link';
import Section from '@polestar/component-warehouse-react/components/section/Section';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import styled from '@emotion/styled';
import { MainAxisAlignments } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { forwardRef, Fragment, useEffect, useRef } from 'react';
import docgenInterface from '@polestar/component-warehouse-react/helpers/docgenInterface';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Emphasis from '@polestar/component-warehouse-react/tokens/emphasis/Emphasis';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import Grids from '@polestar/component-warehouse-react/tokens/grids/Grids';
import Accordion from '@polestar/component-warehouse-react/components/accordion/Accordion';
import AccordionSection from '@polestar/component-warehouse-react/components/accordion/AccordionSection';
import Column from '@polestar/component-warehouse-react/components/column/Column';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import Caption from '@polestar/component-warehouse-react/components/caption/Caption';
import useTrackingData from '@polestar/component-warehouse-react/hooks/useTrackingData';
import { DataLayerAction, DataLayerCategory, DataLayerEvent, DataLayerSection, } from '@polestar/component-warehouse-react/tokens/analytics/Analytics';
import useInView from '@polestar/component-warehouse-react/hooks/useInView';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
/**
 * @internal
 */
export const FooterDoormatListGroupPropsInterface = docgenInterface();
/**
 * @internal
 */
export const ListGroupItemPropsInterface = docgenInterface();
/**
 * @internal
 */
const FooterDoormat = forwardRef(({ listGroups = [], content, ...props }, ref) => {
    var _a;
    const { isKeyboardControlled } = useInteractionMode();
    const { queryMatches } = useMediaQuery();
    const [inViewRef, isInView] = useInView({
        triggerOnce: false,
    });
    const isTrackingDataPushed = useRef(false);
    const combinedRef = useCombinedRef(ref, inViewRef);
    const showAccordion = queryMatches.mobile;
    let wrapper;
    const initialItems = listGroups.slice(0, 1);
    const accordionItems = listGroups.slice(1, listGroups.length);
    const { handleNavigationDrawerTrackingData, pushDataLayer } = useTrackingData(DataLayerCategory.FOOTER);
    useEffect(() => {
        if (!isInView || isTrackingDataPushed.current)
            return;
        pushDataLayer({
            event: DataLayerEvent.SECTION_VISIBLE,
            sectionId: DataLayerSection.WEB_SECTION_FOOTER,
        });
        isTrackingDataPushed.current = true;
    }, [isInView, pushDataLayer]);
    if (showAccordion) {
        wrapper = (React.createElement(Fragment, null,
            content && (React.createElement(Fragment, null,
                content,
                React.createElement(ContentPadding, null))),
            React.createElement(Group, { ...aria({ labelledby: `FooterDoormat-group-initial-header` }) }, (_a = initialItems === null || initialItems === void 0 ? void 0 : initialItems[0]) === null || _a === void 0 ? void 0 : _a.items.map((item, index) => {
                const { label, onClick, ...itemProps } = item;
                const handleClick = (e) => {
                    handleNavigationDrawerTrackingData(label, DataLayerAction.FOOTER_CLICK);
                    onClick === null || onClick === void 0 ? void 0 : onClick(e);
                };
                return (React.createElement(StyledLink, { key: index, onClick: handleClick, ...itemProps, isKeyboardControlled: isKeyboardControlled, variant: LinkVariants.navigation }, label));
            })),
            React.createElement(Accordion, { closeOthersOnOpen: true, "data-type": "wrapper" }, accordionItems === null || accordionItems === void 0 ? void 0 : accordionItems.map(({ items, label: sectionLabel }, index) => (React.createElement(AccordionSection, { key: `dmta-${index}`, title: sectionLabel, optimizeRendering: false }, items === null || items === void 0 ? void 0 : items.map(({ label, onClick, ...itemProps }, index) => {
                const handleAccordionClick = (e) => {
                    handleNavigationDrawerTrackingData(sectionLabel, DataLayerAction.FOOTER_CLICK);
                    onClick === null || onClick === void 0 ? void 0 : onClick(e);
                };
                return (React.createElement(Column, { key: index },
                    React.createElement(StyledLink, { key: index, ...itemProps, onClick: handleAccordionClick, isKeyboardControlled: isKeyboardControlled, variant: LinkVariants.navigation }, label)));
            })))))));
    }
    else {
        wrapper = (React.createElement(StyledGrid, { "data-type": "wrapper" },
            content && (React.createElement(CustomColumn, { span: { mobile: 4, tablet: 5, desktop: 4, desktopWide: 3 } }, content)),
            React.createElement(StyledListGroupWrapper, { columnOffset: Math.max(0, 3 - listGroups.length) }, listGroups === null || listGroups === void 0 ? void 0 : listGroups.map(({ items, label, ...group }, index) => {
                const id = `FooterDoormat-group-${index}-header`;
                if (!(items === null || items === void 0 ? void 0 : items.length)) {
                    return null;
                }
                return (React.createElement(Group, { key: index, ...aria({ labelledby: label && index !== 0 ? id : null }), ...group },
                    label && index !== 0 && React.createElement(StyledCaption, { id: id }, label), items === null || items === void 0 ? void 0 :
                    items.map((item, index) => {
                        const { label, ...itemProps } = item;
                        return (React.createElement(StyledLink, { key: index, ...itemProps, isKeyboardControlled: isKeyboardControlled, variant: LinkVariants.navigation }, label));
                    })));
            }))));
    }
    return (React.createElement(StyledFooterDoormat, { ...props, ref: combinedRef },
        wrapper,
        React.createElement(Fragment, null,
            React.createElement(CustomSpacer, null),
            !showAccordion && (React.createElement(Divider, { mainAxisAlignment: MainAxisAlignments.end, emphasis: Emphasis.low, spacing: "0" })))));
});
FooterDoormat.displayName = 'FooterDoormat';
export default FooterDoormat;
const ContentPadding = styled.div(({ theme }) => css `
    ${responsiveStyles({
    height: theme.footerDoormat.verticalContentPadding,
})}
  `);
const StyledGrid = styled(Grid) `
  grid-gap: ${Spacings.xxLarge} ${Grids.mobile.gutter};

  ${MediaQueries.tablet} {
    grid-gap: ${Spacings.xxLarge} ${Grids.tablet.gutter};
  }

  ${MediaQueries.desktop} {
    grid-gap: ${Grids.desktop.gutter};
  }
`;
const Group = styled.nav `
  display: flex;
  flex-direction: column;
  break-inside: avoid-column;

  ${MediaQueries.tablet} {
    @media (pointer: coarse) {
      padding-bottom: ${createCustomSpacing(40 / 16)};
    }
    padding-bottom: ${createCustomSpacing(28 / 16)};
  }
`;
const StyledFooterDoormat = styled(Section)(({ theme }) => css `
    background-color: ${theme.footerDoormat.backgroundColor};
    transition: ${createCSSTransition('background-color', MotionEasings.easeOut, MotionDurations.normal)};

    padding-inline-start: 0 !important;
    padding-inline-end: 0 !important;

    ${responsiveStyles({
    paddingBlockStart: theme.footerDoormat.padding.top,
    paddingBlockEnd: theme.footerDoormat.padding.bottom,
})}

    & > * {
      padding-inline-start: 0;
      padding-inline-end: 0;
    }
  `);
const StyledListGroupWrapper = styled.div(({ columnOffset }) => css `
    grid-column-start: 1;
    grid-column-end: span 4;
    columns: 4;
    column-gap: ${Spacings.large};

    ${MediaQueries.tablet} {
      grid-column-start: 1;
      grid-column-end: span 8;
      column-gap: ${Spacings.xLarge};
      columns: 3;
    }

    ${MediaQueries.desktop} {
      grid-column-start: ${5 + columnOffset * 2};
      grid-column-end: span ${8 - columnOffset * 2};
      columns: ${3 - columnOffset};
    }

    ${MediaQueries.desktopWide} {
      grid-column-start: ${5 + columnOffset * 2};
      grid-column-end: span ${8 - columnOffset * 2};
      columns: ${4 - columnOffset};
    }
  `);
const StyledCaption = styled(Caption) `
  @media (pointer: coarse) {
    padding: ${createCustomSpacing(11 / 16)} 0;
  }
  padding: ${createCustomSpacing(5 / 16)} 0;
`;
const StyledLink = styled(Link)(({ theme, isKeyboardControlled }) => css `
    /* prevent accidentally triggering hover state (messes with current focus state). */
    pointer-events: ${isKeyboardControlled ? 'none' : 'auto'};
    color: ${theme.footerDoormat.link.color};

    &:hover {
      color: ${theme.footerDoormat.link.hoverColor};
      text-decoration: underline;
    }

    &:focus {
      color: ${theme.footerDoormat.link.focusColor};
      text-decoration: none;
    }

    &:active {
      color: ${theme.footerDoormat.link.activeColor};
      text-decoration: none;
    }
  `);
// A CustomSpacer is needed to remove duplicate padding coming from the Group component
const CustomSpacer = styled(Spacer) `
  height: ${Spacings.none};

  ${MediaQueries.tablet} {
    height: calc(${Spacings.xxxLarge} - ${createCustomSpacing(28 / 16)});
    @media (pointer: coarse) {
      height: calc(${Spacings.xxxLarge} - ${createCustomSpacing(40 / 16)});
    }
  }

  ${MediaQueries.desktop} {
    height: calc(${Spacings.xxxxLarge} - ${createCustomSpacing(28 / 16)});
    @media (pointer: coarse) {
      height: calc(${Spacings.xxxxLarge} - ${createCustomSpacing(40 / 16)});
    }
  }
`;
// If the doormat content is shorter than the banner slot content this is needed for correct spacing
const CustomColumn = styled(GridColumn) `
  ${MediaQueries.desktop} {
    @media (pointer: coarse) {
      padding-bottom: ${createCustomSpacing(40 / 16)};
    }
    padding-bottom: ${createCustomSpacing(28 / 16)};
  }
`;
